import type { FreezeView, RecheckField, RecheckPass, RecheckView } from "../api/types.js";
import type { JRow, Part } from "./judge-model.js";

/**
 * DCTA-188 Change 3 (docs/specs/fe-validator-freeze-dcta188.md) — judge mode's view of a freeze and of the silent re-check:
 * the rule, the field and both values; the re-check's outcome; both passes as the bank's diff compared them. Everything is
 * the bank's (lab/dcta src/payload/freeze-message.ts `freezeView`, src/agents/recheck.ts `recheckView`): nothing is
 * worked out here but the order of rows and a time in seconds. Judge mode only — no customer surface draws any of it.
 * Keys (`fixed:10000:SGD`, `payee:pay_mum005`, `none:<words>`) are text, set as text by the view.
 */

export interface PassGroup {
  readonly title: "First pass" | "Second pass";
  /** A count difference on this pass, in words ("count · draft 1 · validator 2"). */
  readonly count?: string;
  /** Disagreements first, then the rest, each in the bank's order. */
  readonly rows: readonly RecheckField[];
}

const t = (s: string): Part => ({ kind: "text", t: s });
const mono = (s: string): Part => ({ kind: "mono", t: s });
const id = (s: string): Part => ({ kind: "id", t: s });

const FIELDS = ["amount", "beneficiary", "source_account", "asset_class", "when", "count"] as const;
const OUTCOMES = ["cleared", "still_differs", "unavailable", "skipped"] as const;
const RESULTS = ["agree", "disagree", "skipped"] as const;
const SKIPS = ["injection", "firewall_signal", "scam_signal", "tier3", "budget", "disabled", "redraft", "bank_draft"] as const;

const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined);
const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === "string" && (list as readonly string[]).includes(v);
const obj = (v: unknown): Record<string, unknown> | undefined => (typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined);

/** A freeze's `freeze` block as the bank sends it, or undefined for anything that is not one. */
export function asFreeze(u: unknown): FreezeView | undefined {
  const o = obj(u);
  if (o === undefined || !isOneOf(FIELDS, o["field"]) || (o["rendered"] !== "values" && o["rendered"] !== "field")) return undefined;
  const opt = (k: "step_id" | "said" | "drafted" | "about" | "rule_id") => (str(o[k]) === undefined ? {} : { [k]: str(o[k]) as string });
  return { field: o["field"], rendered: o["rendered"], ...opt("step_id"), ...opt("said"), ...opt("drafted"), ...opt("about"), ...opt("rule_id") };
}

function asPass(u: unknown): RecheckPass | undefined {
  const o = obj(u);
  if (o === undefined || str(o["verdict"]) === undefined || !Array.isArray(o["fields"])) return undefined;
  const fields = (o["fields"] as unknown[]).flatMap((f): RecheckField[] => {
    const e = obj(f);
    if (e === undefined || !isOneOf(RESULTS, e["result"])) return [];
    const [step, field, drafter, validator] = [str(e["step_id"]), str(e["field"]), str(e["drafter"]), str(e["validator"])];
    return step === undefined || field === undefined || drafter === undefined || validator === undefined ? [] : [{ step_id: step, field, drafter, validator, result: e["result"] }];
  });
  const disputed = (Array.isArray(o["disputed"]) ? (o["disputed"] as unknown[]) : []).flatMap((d) => {
    const e = obj(d);
    const [step, field] = [str(e?.["step_id"]), str(e?.["field"])];
    return step === undefined || field === undefined ? [] : [{ step_id: step, field }];
  });
  const c = obj(o["count"]);
  const count = c !== undefined && typeof c["drafter"] === "number" && typeof c["validator"] === "number" ? { count: { drafter: c["drafter"], validator: c["validator"] } } : {};
  return { verdict: o["verdict"] as string, disputed, fields, ...count };
}

/** A `validator_recheck` block as the bank sends it, or undefined for anything that is not one. */
export function asRecheck(u: unknown): RecheckView | undefined {
  const o = obj(u);
  if (o === undefined || !isOneOf(OUTCOMES, o["outcome"])) return undefined;
  const first = asPass(o["first"]);
  if (first === undefined) return undefined;
  const second = o["second"] === undefined ? undefined : asPass(o["second"]);
  return {
    rule_id: str(o["rule_id"]) ?? "VAL-RECHECK",
    outcome: o["outcome"],
    first,
    audit_ref: str(o["audit_ref"]) ?? "",
    ...(isOneOf(SKIPS, o["skipped"]) ? { skipped: o["skipped"] } : {}),
    ...(str(o["detail"]) === undefined ? {} : { detail: str(o["detail"]) as string }),
    ...(second === undefined ? {} : { second }),
    ...(typeof o["second_ms"] === "number" && Number.isFinite(o["second_ms"]) ? { second_ms: o["second_ms"] } : {}),
  };
}

/** "Rule VAL-DIFF-AMOUNT · amount · said S$100.00 · draft S$1,000.00" — or "… · field only"; "none" with no rule id. */
export function freezeRow(f: FreezeView | undefined): JRow | undefined {
  if (f === undefined) return undefined;
  const named = f.rendered === "values" && f.said !== undefined && f.drafted !== undefined;
  const values: Part[] = named ? [t(` · ${f.field} · said `), mono(f.said as string), t(" · draft "), mono(f.drafted as string)] : [t(` · ${f.field} · field only`)];
  return { k: "Rule", v: [f.rule_id === undefined ? t("none") : id(f.rule_id), ...values] };
}

const SKIPPED_WORDS: Readonly<Record<(typeof SKIPS)[number], string>> = {
  injection: "injection",
  firewall_signal: "firewall signal",
  scam_signal: "scam signal",
  tier3: "tier 3",
  budget: "budget",
  disabled: "disabled",
  redraft: "a re-drafted answer",
  bank_draft: "a draft the bank built",
};
const OUTCOME_WORDS: Readonly<Record<RecheckView["outcome"], string>> = { cleared: "cleared", still_differs: "still differs", unavailable: "unavailable", skipped: "skipped" };

const seconds = (ms: number | undefined): string | undefined => (ms === undefined ? undefined : `${(ms / 1000).toFixed(1)} s`);

/** "Re-checked once (VAL-RECHECK): still differs · 1.8 s" — or why none ran, "No re-check: scam signal". */
export function recheckRow(r: RecheckView | undefined): JRow | undefined {
  if (r === undefined) return undefined;
  if (r.outcome === "skipped") return { k: "Re-check", v: [t(`No re-check: ${r.skipped === undefined ? "skipped" : SKIPPED_WORDS[r.skipped]}`)] };
  const tail = [r.outcome === "unavailable" ? r.detail : undefined, seconds(r.second_ms)].filter((s): s is string => s !== undefined && s !== "");
  return { k: "Re-check", v: [t("Re-checked once ("), id(r.rule_id), t(`): ${OUTCOME_WORDS[r.outcome]}${tail.map((s) => ` · ${s}`).join("")}`)] };
}

function group(title: PassGroup["title"], p: RecheckPass): PassGroup {
  const rows = [...p.fields.filter((f) => f.result === "disagree"), ...p.fields.filter((f) => f.result !== "disagree")];
  return { title, rows, ...(p.count === undefined ? {} : { count: `count · draft ${String(p.count.drafter)} · validator ${String(p.count.validator)}` }) };
}

/** The two-pass table's groups: First pass, and Second pass only when one ran. */
export function passGroups(r: RecheckView | undefined): PassGroup[] {
  if (r === undefined) return [];
  return [group("First pass", r.first), ...(r.second === undefined ? [] : [group("Second pass", r.second)])];
}

/** Beside a card or question the re-check cleared: "Re-checked once: the first reading differed on s1 · beneficiary; the second agreed (1.6 s)". */
export function clearedLine(r: RecheckView | undefined): string | undefined {
  if (r?.outcome !== "cleared") return undefined;
  const on = r.first.disputed.length > 0 ? r.first.disputed.map((d) => `${d.step_id} · ${d.field}`).join(", ") : r.first.count !== undefined ? "how many things to do" : "what was said";
  const time = seconds(r.second_ms);
  return `Re-checked once: the first reading differed on ${on}; the second agreed${time === undefined ? "" : ` (${time})`}`;
}
